import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import ModuleLayout from '../common/ModuleLayout';
import SafeIcon from '../../common/SafeIcon';

const { FiEdit, FiUser, FiMap, FiZap, FiCalendar, FiClock, FiCheckCircle, FiBookOpen } = FiIcons;

const ModuleFive = ({ data, onSave, completed }) => {
  const [bookType, setBookType] = useState('nonfiction');
  const [fictionData, setFictionData] = useState({
    mainCharacter: '',
    openingSetting: '',
    sensoryDetails: '',
    initialTension: '',
    openingParagraph: ''
  });
  const [nonfictionData, setNonfictionData] = useState({
    hook: '',
    look: '',
    took: '',
    openingParagraph: ''
  });
  const [commitment, setCommitment] = useState({
    date: '',
    time: '',
    location: '',
    committed: false
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (data) {
      if (data.bookType) setBookType(data.bookType);
      if (data.fictionData) setFictionData(prev => ({ ...prev, ...data.fictionData }));
      if (data.nonfictionData) setNonfictionData(prev => ({ ...prev, ...data.nonfictionData }));
      if (data.commitment) setCommitment(prev => ({ ...prev, ...data.commitment }));
    }
  }, [data]);

  const handleFictionChange = (field, value) => {
    setFictionData(prev => ({ ...prev, [field]: value }));
  };

  const handleNonfictionChange = (field, value) => {
    setNonfictionData(prev => ({ ...prev, [field]: value }));
  };

  const handleCommitmentChange = (field, value) => {
    setCommitment(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (markComplete) => {
    setIsLoading(true);
    const dataToSave = { bookType, fictionData, nonfictionData, commitment };
    setTimeout(() => {
      onSave(dataToSave, markComplete);
      setIsLoading(false);
    }, 800);
  };

  const currentPara = bookType === 'fiction' ? fictionData.openingParagraph : nonfictionData.openingParagraph;
  
  const validateFields = () => {
    if (bookType === 'fiction') {
      return fictionData.openingParagraph.trim() !== '' && 
             fictionData.mainCharacter.trim() !== '' && 
             fictionData.openingSetting.trim() !== '';
    } else {
      return nonfictionData.openingParagraph.trim() !== '' && 
             nonfictionData.hook.trim() !== '' && 
             nonfictionData.look.trim() !== '' && 
             nonfictionData.took.trim() !== '';
    }
  };

  const canComplete = validateFields() && commitment.committed;

  return (
    <ModuleLayout
      title="Kickstart the First Chapter"
      description="Break the blank page and begin writing today."
      icon={FiEdit}
      onSave={handleSave}
      completed={completed}
      canComplete={canComplete}
      isLoading={isLoading}
    >
      <div className="space-y-8">
        {/* Step 1: Path Selector */}
        <div className="bg-white rounded-2xl shadow-sm p-8 border border-slate-100">
          <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-6">Step 1: Choose Your Path</label>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setBookType('fiction')}
              className={`p-6 rounded-2xl border-2 transition-all flex flex-col items-center space-y-3 ${bookType === 'fiction' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-100 bg-slate-50 text-slate-400 hover:border-slate-200'}`}
            >
              <SafeIcon icon={FiZap} className="text-2xl" />
              <span className="font-bold">Fiction</span>
            </button>
            <button
              onClick={() => setBookType('nonfiction')}
              className={`p-6 rounded-2xl border-2 transition-all flex flex-col items-center space-y-3 ${bookType === 'nonfiction' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-100 bg-slate-50 text-slate-400 hover:border-slate-200'}`}
            >
              <SafeIcon icon={FiBookOpen} className="text-2xl" />
              <span className="font-bold">Nonfiction</span>
            </button>
          </div>
        </div>

        {/* Step 2: Specific Fields */}
        <AnimatePresence mode="wait">
          {bookType === 'fiction' ? (
            <motion.div
              key="fiction"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              <div className="bg-white rounded-2xl shadow-sm p-8 border border-slate-100">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Main Character</label>
                    <input
                      type="text"
                      value={fictionData.mainCharacter}
                      onChange={(e) => handleFictionChange('mainCharacter', e.target.value)}
                      className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                      placeholder="Who is starting this journey?"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Opening Setting</label>
                    <input
                      type="text"
                      value={fictionData.openingSetting}
                      onChange={(e) => handleFictionChange('openingSetting', e.target.value)}
                      className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                      placeholder="Where does Act 1 begin?"
                    />
                  </div>
                </div>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Sensory Details (List)</label>
                    <textarea
                      value={fictionData.sensoryDetails}
                      onChange={(e) => handleFictionChange('sensoryDetails', e.target.value)}
                      rows={2}
                      className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none"
                      placeholder="Smells, sounds, textures that ground the scene..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Initial Tension</label>
                    <textarea
                      value={fictionData.initialTension}
                      onChange={(e) => handleFictionChange('initialTension', e.target.value)}
                      rows={2}
                      className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none"
                      placeholder="What is the immediate problem or desire?"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="nonfiction"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              <div className="bg-white rounded-2xl shadow-sm p-8 border border-slate-100">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Hook (Problem or Desire)</label>
                    <textarea
                      value={nonfictionData.hook}
                      onChange={(e) => handleNonfictionChange('hook', e.target.value)}
                      rows={2}
                      className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none"
                      placeholder="Grab the reader's attention with their felt need..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Look (Truth or Insight)</label>
                    <textarea
                      value={nonfictionData.look}
                      onChange={(e) => handleNonfictionChange('look', e.target.value)}
                      rows={2}
                      className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none"
                      placeholder="Introduce the gospel-centered solution or insight..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Took (Application)</label>
                    <textarea
                      value={nonfictionData.took}
                      onChange={(e) => handleNonfictionChange('took', e.target.value)}
                      rows={2}
                      className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none"
                      placeholder="How will this chapter change their life today?"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Opening Paragraph Draft */}
        <div className="bg-white rounded-2xl shadow-sm p-8 border border-slate-100">
          <label className="block text-lg font-bold text-slate-800 mb-4 flex items-center">
            <SafeIcon icon={FiEdit} className="mr-2 text-blue-500" />
            Opening Paragraph Draft
          </label>
          <textarea
            value={currentPara}
            onChange={(e) => {
              const val = e.target.value;
              if (bookType === 'fiction') handleFictionChange('openingParagraph', val);
              else handleNonfictionChange('openingParagraph', val);
            }}
            rows={8}
            className="w-full p-6 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none leading-relaxed italic text-lg"
            placeholder="Don't worry about perfection—just begin. You can't edit a blank page!"
          />
        </div>

        {/* Step 3: Sprint Commitment */}
        <div className="bg-white rounded-2xl shadow-sm p-8 border border-slate-100">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-orange-50 text-orange-600 rounded-lg">
              <SafeIcon icon={FiCalendar} />
            </div>
            <h3 className="text-xl font-bold text-slate-800">Writing Sprint Commitment</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div>
              <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Date</label>
              <input
                type="date"
                value={commitment.date}
                onChange={(e) => handleCommitmentChange('date', e.target.value)}
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Time</label>
              <input
                type="time"
                value={commitment.time}
                onChange={(e) => handleCommitmentChange('time', e.target.value)}
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Location</label>
              <input
                type="text"
                value={commitment.location}
                onChange={(e) => handleCommitmentChange('location', e.target.value)}
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                placeholder="e.g., Study, Quiet Corner"
              />
            </div>
          </div>
          <label className="flex items-center cursor-pointer group p-4 bg-blue-50 rounded-2xl border border-blue-100 transition-all hover:bg-blue-100">
            <input
              type="checkbox"
              checked={commitment.committed}
              onChange={(e) => handleCommitmentChange('committed', e.target.checked)}
              className="w-6 h-6 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-4 text-lg font-bold text-blue-900 group-hover:text-blue-700 transition-colors italic">
              "I commit to starting before I feel ready."
            </span>
          </label>
        </div>
      </div>
    </ModuleLayout>
  );
};

export default ModuleFive;